import * as R from "ramda";
import {
  ColumnOrMetricName,
  DimensionColNames,
  getConfig,
  getDataColForMetadata,
  getDimensionColForMetadata,
  getDimensionIconStyle,
} from "@blisspointmedia/bpm-types/dist/Columns";
import { ColumnConfig } from "@blisspointmedia/bpm-types/dist/ColumnConfig";
import { Metric, NativeCurrencyMetric } from "@blisspointmedia/bpm-types/dist/CrossChannel";
import { CompanyFiscalCal } from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import {
  IMP_IN_THOUSANDS_FORMATTER,
  MetricFormatter,
  metricFormatter,
  metricFormatterCAD,
  metricFormatterEUR,
  metricFormatterGBP,
  SPEND_OR_REVENUE_FORMATTER,
  toPrettyNumber,
} from "@blisspointmedia/bpm-types/dist/metricFormatUtils";
import { getMetricAggregation, MetricConfig } from "@blisspointmedia/bpm-types/dist/Metrics";
import { DimensionColumn } from "@blisspointmedia/bpm-types/dist/MetricsTable";
import * as P from "@blisspointmedia/bpm-types/dist/Performance";
import {
  MetricSqlContext,
  resolveMetricParam,
} from "@blisspointmedia/bpm-types/dist/querying/metricSqlParams";
import { App2Channels } from "@blisspointmedia/bpm-types/dist/ScpChannels";
import {
  DELIVERY_CURRENCY_CODE,
  METRIC_TO_PRETTY_NAME,
  PERFORMANCE_CURRENCY_CODE,
} from "../../CrossChannel/crossChannelConstants";
import { formatDateLabel } from "../../utils/date-utils";
import {
  AdInfoRow,
  CDN,
  CellData,
  ColumnMetaData,
  ColumnMetaDataMap,
  DimensionMap,
  getCreativeThumbnail,
} from "../MetricsTable/metricsTableUtils";

const {
  CLICKS,
  IMPRESSIONS,
  VOLUME,
  SPEND,
  REVENUE,
  CPC,
  CPM,
  ROAS,
  CPX,
  CTR,
  CLICKS_CONVERSION_RATE,
  IMPS_CONVERSION_RATE,
  AOV,
} = Metric;

const {
  CPC_NATIVE,
  CPM_NATIVE,
  CPX_NATIVE,
  SPEND_NATIVE,
  REVENUE_NATIVE,
  AOV_NATIVE,
} = NativeCurrencyMetric;

// when possible we prefer decimels are built into formatter
// so that metricFormatter() may be called outside of metrics table
// but may be included to pass to formatValue later

export const sharedDimensionColumnMetaDataMap: Partial<Record<string, ColumnMetaData>> = {
  Date: {
    contentType: "text",
    dimensionVarName: "Date",
    displayName: "Date",
  },
};

// Builds a ColumnMetaData from a metric's bpm-types config; older entries below predate config
// `formatter`/`aggregator`. No `currencyGetter` -- TVAD has no native-currency columns.
const configMetricEntry = (name: string): ColumnMetaData => {
  const cfg = getConfig(name as ColumnOrMetricName) as
    | (ColumnConfig & Partial<MetricConfig>)
    | undefined;
  const agg = getMetricAggregation(name);
  if (!cfg || !agg) {
    throw new Error(`configMetricEntry: ${name} is not a known metric`);
  }
  // `aggregate` is authoritative: encodes multipliers (cpm's x1000), nulls bad denominators.
  const value = (row?: Record<string, string | number>): number | string =>
    (row ? agg.aggregate(row) : null) ?? "--";

  return {
    displayName: cfg.prettyName,
    formatValue: cfg.formatter,
    aggregator: value,
    fetchGetter: value,
    requiredTotalsColumns: [...agg.requiredColumns],
    minIsBest: !cfg.isHigherValueBetter,
  };
};

// TRP depends on the audience's Nielsen UE, so the page builds it for its audience (see
// SingleChannelPage). Display fields come from bpm-types config, as in `configMetricEntry`.
export const targetRatingPointsEntry = (ctx: MetricSqlContext): ColumnMetaData => {
  const cfg = getConfig("targetRatingPoints") as ColumnConfig & Partial<MetricConfig>;
  const ue = resolveMetricParam("nielsenUeThousands", ctx);
  const trp = (row?: Record<string, string | number>): number | string => {
    const imps = Number(row?.impressions);
    return ue && Number.isFinite(imps) ? imps / (ue * 10) : "--";
  };
  return {
    displayName: cfg.prettyName,
    formatValue: cfg.formatter,
    aggregator: trp,
    fetchGetter: trp,
    requiredTotalsColumns: ["impressions"],
    minIsBest: !cfg.isHigherValueBetter,
  };
};

export const sharedColumnMetaDataMap: Partial<Record<P.ColumnType, ColumnMetaData>> = {
  [CLICKS]: {
    displayName: METRIC_TO_PRETTY_NAME[CLICKS],
    formatValue: metricFormatter(CLICKS),
  },
  [SPEND]: {
    displayName: METRIC_TO_PRETTY_NAME[SPEND],
    formatValue: metricFormatter(SPEND),
  },
  [IMPRESSIONS]: {
    displayName: "Impressions (000s)",
    formatValue: IMP_IN_THOUSANDS_FORMATTER,
  },
  responding_impressions: {
    displayName: "Responding Impressions (000s)",
    formatValue: IMP_IN_THOUSANDS_FORMATTER,
  },
  residential_impressions: {
    displayName: "Residential Impressions (000s)",
    formatValue: IMP_IN_THOUSANDS_FORMATTER,
  },
  respIndex: {
    displayName: "Response Index",
    aggregator: () => 1,
    formatValue: toPrettyNumber,
    decimals: 2,
    fetchGetter: (fetch, agg) =>
      fetch &&
      agg &&
      fetch.responding_impressions &&
      agg.responding_impressions &&
      fetch.residential_impressions &&
      agg.residential_impressions
        ? (fetch.responding_impressions as number) /
          (agg.responding_impressions as number) /
          ((fetch.residential_impressions as number) / (agg.residential_impressions as number))
        : "--",
    requiredTotalsColumns: ["responding_impressions", "residential_impressions"],
  },
  [VOLUME]: {
    displayName: METRIC_TO_PRETTY_NAME[VOLUME],
    formatValue: metricFormatter(VOLUME),
  },
  [REVENUE]: {
    displayName: METRIC_TO_PRETTY_NAME[REVENUE],
    formatValue: metricFormatter(REVENUE),
  },
  [CPC]: {
    displayName: METRIC_TO_PRETTY_NAME[CPC],
    formatValue: metricFormatter(CPC),
    aggregator: agg =>
      agg[CLICKS] && agg[SPEND] ? (agg[SPEND] as number) / (agg[CLICKS] as number) : "--",
    fetchGetter: fetch =>
      fetch && fetch[CLICKS] && fetch[SPEND]
        ? (fetch[SPEND] as number) / (fetch[CLICKS] as number)
        : "--",
    requiredTotalsColumns: [CLICKS, SPEND],
  },
  [CPM]: {
    displayName: METRIC_TO_PRETTY_NAME[CPM],
    formatValue: metricFormatter(CPM),
    aggregator: agg =>
      agg[IMPRESSIONS] && agg[SPEND]
        ? ((agg[SPEND] as number) * 1000) / (agg[IMPRESSIONS] as number)
        : "--",
    fetchGetter: fetch =>
      fetch && fetch[IMPRESSIONS] && fetch[SPEND]
        ? ((fetch[SPEND] as number) * 1000) / (fetch[IMPRESSIONS] as number)
        : "--",
    requiredTotalsColumns: [IMPRESSIONS, SPEND],
  },
  [ROAS]: {
    displayName: METRIC_TO_PRETTY_NAME[ROAS],
    formatValue: metricFormatter(ROAS),
    aggregator: agg =>
      agg[REVENUE] && agg[SPEND] ? (agg[REVENUE] as number) / (agg[SPEND] as number) : "--",
    fetchGetter: fetch =>
      fetch && fetch[REVENUE] && fetch[SPEND]
        ? (fetch[REVENUE] as number) / (fetch[SPEND] as number)
        : 0,
    requiredTotalsColumns: [REVENUE, SPEND],
  },
  [CPX]: {
    displayName: METRIC_TO_PRETTY_NAME[CPX],
    formatValue: metricFormatter(CPX),
    aggregator: agg =>
      agg[VOLUME] && agg[SPEND] ? (agg[SPEND] as number) / (agg[VOLUME] as number) : "--",
    fetchGetter: fetch =>
      fetch && fetch[VOLUME] && fetch[SPEND]
        ? (fetch[SPEND] as number) / (fetch[VOLUME] as number)
        : "--",
    requiredTotalsColumns: [VOLUME, SPEND],
  },
  [CTR]: {
    displayName: METRIC_TO_PRETTY_NAME[CTR],
    formatValue: metricFormatter(CTR),
    aggregator: agg =>
      agg[CLICKS] && agg[IMPRESSIONS]
        ? (agg[CLICKS] as number) / (agg[IMPRESSIONS] as number)
        : "--",
    fetchGetter: fetch =>
      fetch && fetch[CLICKS] && fetch[IMPRESSIONS]
        ? (fetch[CLICKS] as number) / (fetch[IMPRESSIONS] as number)
        : "--",
    requiredTotalsColumns: [CLICKS, IMPRESSIONS],
  },
  [CLICKS_CONVERSION_RATE]: {
    displayName: METRIC_TO_PRETTY_NAME[CLICKS_CONVERSION_RATE],
    formatValue: metricFormatter(CLICKS_CONVERSION_RATE),
    aggregator: agg =>
      agg[VOLUME] && agg[CLICKS] ? (agg[VOLUME] as number) / (agg[CLICKS] as number) : "--",
    fetchGetter: fetch =>
      fetch && fetch[VOLUME] && fetch[CLICKS]
        ? (fetch[VOLUME] as number) / (fetch[CLICKS] as number)
        : "--",
    requiredTotalsColumns: [VOLUME, CLICKS],
  },
  [IMPS_CONVERSION_RATE]: {
    displayName: METRIC_TO_PRETTY_NAME[IMPS_CONVERSION_RATE],
    formatValue: metricFormatter(IMPS_CONVERSION_RATE),
    aggregator: agg =>
      agg[VOLUME] && agg[IMPRESSIONS]
        ? (agg[VOLUME] as number) / (agg[IMPRESSIONS] as number)
        : "--",
    fetchGetter: fetch =>
      fetch && fetch[VOLUME] && fetch[IMPRESSIONS]
        ? (fetch[VOLUME] as number) / (fetch[IMPRESSIONS] as number)
        : "--",
    requiredTotalsColumns: [VOLUME, IMPRESSIONS],
  },
  [SPEND_NATIVE]: {
    displayName: METRIC_TO_PRETTY_NAME[SPEND_NATIVE],
    formatValue: metricFormatter(SPEND_NATIVE),
    currencyGetter: col =>
      col[DELIVERY_CURRENCY_CODE] === "" ? "USD" : (col[DELIVERY_CURRENCY_CODE] as string),
    requiredTotalsColumns: [DELIVERY_CURRENCY_CODE, SPEND_NATIVE],
  },
  [REVENUE_NATIVE]: {
    displayName: METRIC_TO_PRETTY_NAME[REVENUE_NATIVE],
    formatValue: metricFormatter(REVENUE_NATIVE),
    currencyGetter: col =>
      col[PERFORMANCE_CURRENCY_CODE] === "" ? "USD" : (col[PERFORMANCE_CURRENCY_CODE] as string),
    requiredTotalsColumns: [PERFORMANCE_CURRENCY_CODE, REVENUE_NATIVE],
  },
  [CPC_NATIVE]: {
    displayName: METRIC_TO_PRETTY_NAME[CPC_NATIVE],
    formatValue: metricFormatter(CPC_NATIVE),
    aggregator: agg =>
      agg[CLICKS] && agg[SPEND_NATIVE]
        ? (agg[SPEND_NATIVE] as number) / (agg[CLICKS] as number)
        : "--",
    fetchGetter: fetch =>
      fetch[CLICKS] && fetch[SPEND_NATIVE]
        ? (fetch[SPEND_NATIVE] as number) / (fetch[CLICKS] as number)
        : "--",
    requiredTotalsColumns: [CLICKS, SPEND_NATIVE, DELIVERY_CURRENCY_CODE],
    currencyGetter: col =>
      col[DELIVERY_CURRENCY_CODE] === "" ? "USD" : (col[DELIVERY_CURRENCY_CODE] as string),
  },
  [CPM_NATIVE]: {
    displayName: METRIC_TO_PRETTY_NAME[CPM_NATIVE],
    formatValue: metricFormatter(CPM_NATIVE),
    aggregator: agg =>
      agg[IMPRESSIONS] && agg[SPEND_NATIVE]
        ? ((agg[SPEND_NATIVE] as number) * 1000) / (agg[IMPRESSIONS] as number)
        : "--",
    fetchGetter: fetch =>
      fetch[IMPRESSIONS] && fetch[SPEND_NATIVE]
        ? ((fetch[SPEND_NATIVE] as number) * 1000) / (fetch[IMPRESSIONS] as number)
        : "--",
    requiredTotalsColumns: [IMPRESSIONS, SPEND_NATIVE, DELIVERY_CURRENCY_CODE],
    currencyGetter: col =>
      col[DELIVERY_CURRENCY_CODE] === "" ? "USD" : (col[DELIVERY_CURRENCY_CODE] as string),
  },
  [CPX_NATIVE]: {
    displayName: METRIC_TO_PRETTY_NAME[CPX_NATIVE],
    formatValue: metricFormatter(CPX_NATIVE),
    aggregator: agg =>
      agg[VOLUME] && agg[SPEND_NATIVE]
        ? (agg[SPEND_NATIVE] as number) / (agg[VOLUME] as number)
        : "--",
    fetchGetter: fetch =>
      fetch[VOLUME] && fetch[SPEND_NATIVE]
        ? (fetch[SPEND_NATIVE] as number) / (fetch[VOLUME] as number)
        : "--",
    requiredTotalsColumns: [VOLUME, SPEND_NATIVE, DELIVERY_CURRENCY_CODE],
    currencyGetter: col =>
      col[DELIVERY_CURRENCY_CODE] === "" ? "USD" : (col[DELIVERY_CURRENCY_CODE] as string),
  },
  [AOV_NATIVE]: {
    aggregator: (agg => {
      const volume = agg.conversions || agg.volume;
      return volume && (volume as number) > 0 && agg[REVENUE_NATIVE]
        ? (agg[REVENUE_NATIVE] as number) / R.defaultTo(0.0, volume as number)
        : "--";
    }) as (agg: Record<string, string | number>) => number | string,
    displayName: "AOV Raw",
    fetchGetter: (fetch => {
      const volume = fetch.conversions || fetch.volume;
      return volume && (volume as number) > 0 && fetch[REVENUE_NATIVE]
        ? (fetch[REVENUE_NATIVE] as number) / R.defaultTo(0.0, volume as number)
        : "--";
    }) as (
      fetch: Record<string, string | number>,
      aggregatedData: Record<string, any>
    ) => number | string,
    formatValue: SPEND_OR_REVENUE_FORMATTER,
    currencyGetter: col =>
      col[PERFORMANCE_CURRENCY_CODE] === "" ? "USD" : (col[PERFORMANCE_CURRENCY_CODE] as string),
    requiredTotalsColumns: [PERFORMANCE_CURRENCY_CODE, REVENUE_NATIVE, "conversions", "volume"],
  },
  [AOV]: {
    aggregator: (agg => {
      const volume = agg.conversions || agg.volume;
      return volume && (volume as number) > 0 && agg.revenue
        ? (agg.revenue as number) / R.defaultTo(0.0, volume as number)
        : "--";
    }) as (agg: Record<string, string | number>) => number | string,
    displayName: "AOV",
    fetchGetter: (fetch => {
      const volume = fetch.conversions || fetch.volume;
      return volume && (volume as number) > 0 && fetch.revenue
        ? (fetch.revenue as number) / R.defaultTo(0.0, volume as number)
        : "--";
    }) as (
      fetch: Record<string, string | number>,
      aggregatedData: Record<string, any>
    ) => number | string,
    formatValue: SPEND_OR_REVENUE_FORMATTER,
    requiredTotalsColumns: ["revenue", "conversions", "volume"],
  },
  volume_direct: {
    displayName: "KPI Volume, Direct",
    formatValue: metricFormatter(VOLUME),
  },
  volume_direct_unadjusted: {
    displayName: "KPI Volume, Direct Unadjusted",
    formatValue: metricFormatter(VOLUME),
  },
  volume_direct_incremental: {
    displayName: "KPI Volume, Direct Incremental",
    formatValue: metricFormatter(VOLUME),
  },
  volume_full_funnel: {
    displayName: "KPI Volume, Full Funnel",
    formatValue: metricFormatter(VOLUME),
  },
  volume_full_funnel_unadjusted: {
    displayName: "KPI Volume, Full Funnel Unadjusted",
    formatValue: metricFormatter(VOLUME),
  },
  volume_full_funnel_incremental: {
    displayName: "KPI Volume, Full Funnel Incremental",
    formatValue: metricFormatter(VOLUME),
  },
  revenue_direct: {
    displayName: "KPI Revenue, Direct",
    formatValue: metricFormatter(REVENUE),
  },
  revenue_direct_unadjusted: {
    displayName: "KPI Revenue, Direct Unadjusted",
    formatValue: metricFormatter(REVENUE),
  },
  revenue_direct_incremental: {
    displayName: "KPI Revenue, Direct Incremental",
    formatValue: metricFormatter(REVENUE),
  },
  revenue_full_funnel: {
    displayName: "KPI Revenue, Full Funnel",
    formatValue: metricFormatter(REVENUE),
  },
  revenue_full_funnel_unadjusted: {
    displayName: "KPI Revenue, Full Funnel Unadjusted",
    formatValue: metricFormatter(REVENUE),
  },
  revenue_full_funnel_incremental: {
    displayName: "KPI Revenue, Full Funnel Incremental",
    formatValue: metricFormatter(REVENUE),
  },
  video_views: {
    displayName: "Video Views",
    formatValue: toPrettyNumber,
  },
  videos_watched_at_25p: {
    displayName: "Videos Watched at 25%",
    formatValue: toPrettyNumber,
  },
  videos_watched_at_50p: {
    displayName: "Videos Watched at 50%",
    formatValue: toPrettyNumber,
  },
  videos_watched_at_75p: {
    displayName: "Videos Watched at 75%",
    formatValue: toPrettyNumber,
  },
  videos_watched_at_100p: {
    displayName: "Videos Watched at 100%",
    formatValue: toPrettyNumber,
  },
  video_plays: {
    displayName: "Video Plays",
    formatValue: toPrettyNumber,
  },
  video_views_2_sec: {
    displayName: "Videos Views 2 Seconds",
    formatValue: toPrettyNumber,
  },
  video_views_3_sec: {
    displayName: "Videos Views 3 Seconds",
    formatValue: toPrettyNumber,
  },
  video_views_5_sec: {
    displayName: "Videos Views 5 Seconds",
    formatValue: toPrettyNumber,
  },
  video_views_6_sec: {
    displayName: "Videos Views 6 Seconds",
    formatValue: toPrettyNumber,
  },
  video_views_10_sec: {
    displayName: "Videos Views 10 Seconds",
    formatValue: toPrettyNumber,
  },
  video_views_15_sec: {
    displayName: "Videos Views 15 Seconds",
    formatValue: toPrettyNumber,
  },
  video_thruplays: {
    displayName: "Videos ThruPlays",
    formatValue: toPrettyNumber,
  },
  acos: {
    aggregator: agg =>
      agg.spend && (agg.spend as number) > 0 && agg.revenue
        ? Number(agg.spend as number) / R.defaultTo(0.0, agg.revenue as number)
        : "--",
    displayName: "ACOS",
    fetchGetter: fetch =>
      fetch.spend && (fetch.spend as number) > 0 && fetch.revenue
        ? Number(fetch.spend as number) / R.defaultTo(0.0, fetch.revenue as number)
        : "--",
    formatValue: metricFormatter("acos"),
    requiredTotalsColumns: ["revenue", "spend"],
  },
  deliveries: {
    displayName: "Deliveries",
    formatValue: metricFormatter(CLICKS),
  },
  sends: {
    displayName: "Sends",
    formatValue: metricFormatter(CLICKS),
  },
  opens: {
    displayName: "Opens",
    formatValue: metricFormatter(CLICKS),
  },
  unsubscribes: {
    displayName: "Unsubscribes",
    formatValue: metricFormatter(CLICKS),
  },
  bounces: {
    displayName: "Bounces",
    formatValue: metricFormatter(CLICKS),
  },
  spam_abuse_reports: {
    displayName: "Spam Reports",
    formatValue: metricFormatter(CLICKS),
  },
  ctrEmail: {
    displayName: METRIC_TO_PRETTY_NAME.ctrEmail,
    formatValue: metricFormatter("ctrEmail"),
    aggregator: agg =>
      agg[CLICKS] && agg.deliveries ? (agg[CLICKS] as number) / (agg.deliveries as number) : "--",
    fetchGetter: fetch =>
      fetch[CLICKS] && fetch.deliveries
        ? (fetch[CLICKS] as number) / (fetch.deliveries as number)
        : "--",
    requiredTotalsColumns: [CLICKS, "deliveries"],
  },
  ctor: {
    displayName: "CTOR",
    formatValue: metricFormatter(CTR),
    aggregator: agg =>
      agg[CLICKS] && agg.opens ? (agg[CLICKS] as number) / (agg.opens as number) : "--",
    fetchGetter: fetch =>
      fetch[CLICKS] && fetch.opens ? (fetch[CLICKS] as number) / (fetch.opens as number) : "--",
    requiredTotalsColumns: [CLICKS, "opens"],
  },
  revenuePerDelivered: {
    displayName: "Revenue per Email",
    formatValue: metricFormatter("revenuePerDelivered"),
    aggregator: agg =>
      agg.revenue && agg.deliveries ? (agg.revenue as number) / (agg.deliveries as number) : "--",
    fetchGetter: fetch =>
      fetch.revenue && fetch.deliveries
        ? (fetch.revenue as number) / (fetch.deliveries as number)
        : "--",
    requiredTotalsColumns: ["revenue", "deliveries"],
  },
  revenuePerDeliveredNative: {
    aggregator: agg =>
      agg[REVENUE_NATIVE] && agg.deliveries
        ? (agg[REVENUE_NATIVE] as number) / (agg.deliveries as number)
        : "--",
    displayName: "Revenue per Email Raw",
    fetchGetter: fetch =>
      fetch[REVENUE_NATIVE] && fetch.deliveries
        ? (fetch[REVENUE_NATIVE] as number) / (fetch.deliveries as number)
        : "--",
    formatValue: SPEND_OR_REVENUE_FORMATTER,
    currencyGetter: col =>
      col[PERFORMANCE_CURRENCY_CODE] === "" ? "USD" : (col[PERFORMANCE_CURRENCY_CODE] as string),
    requiredTotalsColumns: [PERFORMANCE_CURRENCY_CODE, REVENUE_NATIVE, "deliveries"],
  },
  bounceRate: {
    displayName: "Bounce Rate",
    formatValue: metricFormatter("bounceRate"),
    aggregator: agg =>
      agg.bounces && agg.deliveries ? (agg.bounces as number) / (agg.deliveries as number) : "--",
    fetchGetter: fetch =>
      fetch.bounces && fetch.deliveries
        ? (fetch.bounces as number) / (fetch.deliveries as number)
        : "--",
    requiredTotalsColumns: ["bounces", "deliveries"],
  },
  openRate: {
    displayName: "Open Rate",
    formatValue: metricFormatter("openRate"),
    aggregator: agg =>
      agg.opens && agg.deliveries ? (agg.opens as number) / (agg.deliveries as number) : "--",
    fetchGetter: fetch =>
      fetch.opens && fetch.deliveries
        ? (fetch.opens as number) / (fetch.deliveries as number)
        : "--",
    requiredTotalsColumns: ["opens", "deliveries"],
  },
  unsubscribeRate: {
    displayName: "Unsubscribe Rate",
    formatValue: metricFormatter("unsubscribeRate"),
    aggregator: agg =>
      agg.unsubscribes && agg.deliveries
        ? (agg.unsubscribes as number) / (agg.deliveries as number)
        : "--",
    fetchGetter: fetch =>
      fetch.unsubscribes && fetch.deliveries
        ? (fetch.unsubscribes as number) / (fetch.deliveries as number)
        : "--",
    requiredTotalsColumns: ["unsubscribes", "deliveries"],
  },
  clickToOpenRate: {
    displayName: "Click to Open Rate",
    formatValue: metricFormatter("clickToOpenRate"),
    aggregator: agg =>
      agg.clicks && agg.opens ? (agg.clicks as number) / (agg.opens as number) : "--",
    fetchGetter: fetch =>
      fetch.clicks && fetch.opens ? (fetch.clicks as number) / (fetch.opens as number) : "--",
    requiredTotalsColumns: ["clicks", "opens"],
  },
  spamReportRate: {
    displayName: "Spam Report Rate",
    formatValue: metricFormatter("spamReportRate"),
    aggregator: agg =>
      agg.spam_abuse_reports && agg.deliveries
        ? (agg.spam_abuse_reports as number) / (agg.deliveries as number)
        : "--",
    fetchGetter: fetch =>
      fetch.spam_abuse_reports && fetch.deliveries
        ? (fetch.spam_abuse_reports as number) / (fetch.deliveries as number)
        : "--",
    requiredTotalsColumns: ["deliveries", "spam_abuse_reports"],
  },
  // TODO remove once streamging / linear are migrated to standard SCP
  cost: {
    displayName: METRIC_TO_PRETTY_NAME[SPEND],
    formatValue: metricFormatter(SPEND),
  },
  count: {
    displayName: "Impressions",
    formatValue: metricFormatter(VOLUME),
  },
  eCpm: {
    displayName: "eCPM",
    formatValue: metricFormatter(CPM),
    aggregator: agg =>
      agg.count && agg.cost ? ((agg.cost as number) * 1000) / (agg.count as number) : "--",
    fetchGetter: fetch =>
      fetch.count && fetch.cost ? ((fetch.cost as number) * 1000) / (fetch.count as number) : "--",
    requiredTotalsColumns: ["cost", "count"],
  },
  youtubeCpm: {
    displayName: "CPM",
    formatValue: metricFormatter(CPM),
    aggregator: agg =>
      agg.count && agg.cost ? ((agg.cost as number) * 1000) / (agg.count as number) : "--",
    fetchGetter: fetch =>
      fetch.count && fetch.cost ? ((fetch.cost as number) * 1000) / (fetch.count as number) : "--",
    requiredTotalsColumns: ["cost", "count"],
  },
  cpv: {
    displayName: "Cost per View",
    formatValue: metricFormatter("cpv"),
    aggregator: agg =>
      agg.video_views && (agg.video_views as number) > 0 && agg.spend
        ? (agg.spend as number) / (agg.video_views as number)
        : "--",
    fetchGetter: fetch =>
      fetch.video_views && (fetch.video_views as number) > 0 && fetch.spend
        ? (fetch.spend as number) / (fetch.video_views as number)
        : "--",
    requiredTotalsColumns: ["spend", "video_views"],
  },
  viewsConversionRate: {
    aggregator: agg =>
      agg.video_views && (agg.video_views as number) > 0 && agg.volume
        ? (agg.volume as number) / R.defaultTo(0.0, agg.video_views as number)
        : "--",
    displayName: "Views Conversions Rate",
    fetchGetter: fetch =>
      fetch.video_views && (fetch.video_views as number) > 0 && fetch.volume
        ? (fetch.volume as number) / R.defaultTo(0.0, fetch.video_views as number)
        : "--",
    formatValue: metricFormatter("viewsConversionRate"),
    requiredTotalsColumns: ["video_views", "volume"],
  },
  viewthroughRate: {
    displayName: "View Through Rate",
    aggregator: agg =>
      agg.impressions && (agg.impressions as number) > 0 && agg.video_views
        ? (agg.video_views as number) / R.defaultTo(0.0, agg.impressions as number)
        : "--",
    fetchGetter: fetch =>
      fetch.impressions && (fetch.impressions as number) > 0 && fetch.video_views
        ? (fetch.video_views as number) / R.defaultTo(0.0, fetch.impressions as number)
        : "--",
    formatValue: metricFormatter("viewthroughRate"),
    requiredTotalsColumns: ["impressions", "video_views"],
  },
  impression_share: {
    displayName: "Impression Share",
    formatValue: metricFormatter("impression_share"),
    aggregator: agg =>
      agg && (agg.impressions as number) > 0 && agg.weightedImpressionShare
        ? (agg.weightedImpressionShare as number) / (agg.impressions as number)
        : "--",
    requiredTotalsColumns: [
      "impressions",
      {
        dataVarName: "weightedImpressionShare",
        fetchGetter: fetch =>
          R.defaultTo(0, fetch.impressions as number) *
          R.defaultTo(0, fetch.impression_share as number),
      },
    ],
  },

  // --- TVAD computed metrics (streaming / audio / display) ---
  // Values sourced from bpm-types config via configMetricEntry.
  roasDirect: configMetricEntry("roasDirect"),
  roasDirectUnadjusted: configMetricEntry("roasDirectUnadjusted"),
  roasDirectIncremental: configMetricEntry("roasDirectIncremental"),
  roasFullFunnel: configMetricEntry("roasFullFunnel"),
  roasFullFunnelUnadjusted: configMetricEntry("roasFullFunnelUnadjusted"),
  roasFullFunnelIncremental: configMetricEntry("roasFullFunnelIncremental"),

  cpxDirect: configMetricEntry("cpxDirect"),
  cpxDirectUnadjusted: configMetricEntry("cpxDirectUnadjusted"),
  cpxDirectIncremental: configMetricEntry("cpxDirectIncremental"),
  cpxFullFunnel: configMetricEntry("cpxFullFunnel"),
  cpxFullFunnelUnadjusted: configMetricEntry("cpxFullFunnelUnadjusted"),
  cpxFullFunnelIncremental: configMetricEntry("cpxFullFunnelIncremental"),

  impsConversionRateDirect: configMetricEntry("impsConversionRateDirect"),
  impsConversionRateDirectUnadjusted: configMetricEntry("impsConversionRateDirectUnadjusted"),
  impsConversionRateDirectIncremental: configMetricEntry("impsConversionRateDirectIncremental"),
  impsConversionRateFullFunnel: configMetricEntry("impsConversionRateFullFunnel"),
  impsConversionRateFullFunnelUnadjusted: configMetricEntry(
    "impsConversionRateFullFunnelUnadjusted"
  ),
  impsConversionRateFullFunnelIncremental: configMetricEntry(
    "impsConversionRateFullFunnelIncremental"
  ),

  percentIncremental: configMetricEntry("percentIncremental"),
  percentDirectIncremental: configMetricEntry("percentDirectIncremental"),

  avgRevenueDirect: configMetricEntry("avgRevenueDirect"),
  avgRevenueDirectIncremental: configMetricEntry("avgRevenueDirectIncremental"),
  avgRevenueFullFunnel: configMetricEntry("avgRevenueFullFunnel"),
  avgRevenueFullFunnelIncremental: configMetricEntry("avgRevenueFullFunnelIncremental"),

  rated_spend: {
    displayName: "Rated Spend",
    formatValue: metricFormatter(SPEND),
  },
  spots: {
    displayName: "Spots",
    formatValue: toPrettyNumber,
  },

  eCPM: configMetricEntry("eCPM"),
  cpxFractional: configMetricEntry("cpxFractional"),
  targetRatingPoints: targetRatingPointsEntry({}),
  linearRespIndex: {
    displayName: "Linear Resp. Index",
    aggregator: () => 1,
    decimals: 2,
    fetchGetter: (
      fetch: Record<string, string | number>,
      agg: Record<string, any>
    ): number | string => {
      const responseRatioAvg = (agg.responseRatioAvg as number) / agg.numRows;
      if (fetch.rated_ad_raw_effect && fetch.impressions && responseRatioAvg > 0) {
        const responseRatio =
          (fetch.impressions as number) > 0
            ? (fetch.rated_ad_raw_effect as number) / (fetch.impressions as number)
            : 0;
        const responseIndex = responseRatio / responseRatioAvg;
        return responseIndex === 0 ? "--" : responseIndex;
      }
      return "--";
    },
    formatValue: toPrettyNumber,
    requiredTotalsColumns: [
      "impressions",
      "rated_ad_raw_effect",
      "numRows",
      {
        // Zero-impression rows contribute 0 and still count toward `numRows`, matching the SQL's
        // `else 0` inside `avg(...) over ()`.
        dataVarName: "responseRatioAvg",
        fetchGetter: fetch =>
          (fetch.impressions as number) > 0
            ? (fetch.rated_ad_raw_effect as number) / (fetch.impressions as number)
            : 0,
      },
    ],
  },
};

const makeCurrencyOverrideMap = (
  formatter: MetricFormatter
): Partial<Record<P.ColumnType, ColumnMetaData>> => ({
  [SPEND]: {
    ...(sharedColumnMetaDataMap[SPEND] as ColumnMetaData),
    formatValue: formatter(SPEND),
  },
  [REVENUE]: {
    ...(sharedColumnMetaDataMap[REVENUE] as ColumnMetaData),
    formatValue: formatter(REVENUE),
  },
  [CPC]: {
    ...(sharedColumnMetaDataMap[CPC] as ColumnMetaData),
    formatValue: formatter(CPC),
  },
  [CPM]: {
    ...(sharedColumnMetaDataMap[CPM] as ColumnMetaData),
    formatValue: formatter(CPM),
  },
  [CPX]: {
    ...(sharedColumnMetaDataMap[CPX] as ColumnMetaData),
    formatValue: formatter(CPX),
  },
  costPerConversion: {
    ...(sharedColumnMetaDataMap.costPerConversion as ColumnMetaData),
    formatValue: formatter("costPerConversion"),
  },
  revenuePerDelivered: {
    ...(sharedColumnMetaDataMap.revenuePerDelivered as ColumnMetaData),
    formatValue: formatter("revenuePerDelivered"),
  },
});

const CURRENCY_OVERRIDE_MAPS: Record<string, Partial<Record<P.ColumnType, ColumnMetaData>>> = {
  CAD: makeCurrencyOverrideMap(metricFormatterCAD),
  GBP: makeCurrencyOverrideMap(metricFormatterGBP),
  EUR: makeCurrencyOverrideMap(metricFormatterEUR),
};

export const getCompanyChannelColumnMetaData = (
  columnMetaDataMap: Partial<Record<P.ColumnType, ColumnMetaData>>,
  currencyCode: string
): Partial<Record<P.ColumnType, ColumnMetaData>> => {
  const overrideMap = CURRENCY_OVERRIDE_MAPS[currencyCode];
  if (!overrideMap) {
    return columnMetaDataMap;
  }
  const target = { ...columnMetaDataMap };
  Object.keys(target).forEach(key => {
    if (overrideMap.hasOwnProperty(key)) {
      target[key] = overrideMap[key];
    }
  });
  return target;
};

// Entries that depend on metric context (today: the audience), keyed by metric.
const CONTEXT_ENTRY_BUILDERS: Partial<
  Record<P.ColumnType, (ctx: MetricSqlContext) => ColumnMetaData>
> = {
  targetRatingPoints: targetRatingPointsEntry,
};

// Like getCompanyChannelColumnMetaData, but swaps in the entries built for `ctx`.
export const getContextColumnMetaData = <M extends ColumnMetaDataMap>(
  columnMetaDataMap: M,
  ctx: MetricSqlContext
): M => {
  const target = { ...columnMetaDataMap };
  Object.entries(CONTEXT_ENTRY_BUILDERS).forEach(([key, buildEntry]) => {
    if (buildEntry && target.hasOwnProperty(key)) {
      target[key] = buildEntry(ctx);
    }
  });
  return target;
};
// Can replace all other versions of this function, TODO delete others
export const getDimensionCell = (
  dimensionData: DimensionMap,
  dimensionHeader: DimensionColumn,
  fiscalCal: CompanyFiscalCal,
  adInfoMap?: Record<string, AdInfoRow>,
  company?: string
): CellData => {
  const { dimensionVarName, dimensionTypeName } = dimensionHeader;
  const resolvedDimensionData = dimensionData as Record<DimensionColNames, string>;
  const dimensionValue = resolvedDimensionData[dimensionVarName];
  const defaultCell = {
    value: dimensionValue,
    label: dimensionValue,
  };
  if (dimensionTypeName === "Platform" && dimensionValue) {
    const parsedDimensionValue = dimensionValue.replace(/\s+/g, "");
    const url = `${CDN}/assets/img/platforms/${parsedDimensionValue}.png`;
    return {
      label: dimensionValue,
      overlayText: dimensionValue,
      value: dimensionValue,
      url,
    };
  } else if (dimensionTypeName === "Creative" && dimensionValue) {
    const { isci } = resolvedDimensionData;
    return {
      label: dimensionValue,
      overlayText: dimensionValue,
      value: dimensionValue,
      url: company && isci ? getCreativeThumbnail(company, isci) : undefined,
    };
  } else if (
    (dimensionTypeName === "Network Name" ||
      dimensionTypeName === "Description" ||
      dimensionTypeName === "Campaign ID") &&
    dimensionValue
  ) {
    const { network_short_code, network_name } = resolvedDimensionData;
    const networkCode =
      network_short_code && network_short_code !== "Unlabeled" ? network_short_code : network_name;
    return {
      label: dimensionValue,
      overlayText: networkCode,
      value: dimensionValue,
      url: company && networkCode ? `${CDN}/networks/${networkCode}.png` : undefined,
    };
  } else if (dimensionTypeName === "Network Group" && dimensionValue) {
    return {
      label: dimensionValue,
      overlayText: dimensionValue,
      value: dimensionValue,
      url:
        company && dimensionValue
          ? `${CDN}/networks/${dimensionValue.toUpperCase()}.png`
          : undefined,
    };
  } else if ((dimensionTypeName === "Device" || dimensionTypeName === "OS") && dimensionValue) {
    return {
      label: dimensionValue,
      overlayText: dimensionValue,
      value: dimensionValue,
      url: `${CDN}/networks/${dimensionValue.toUpperCase()}.png`,
    };
  } else if (dimensionTypeName === "Retailer" && dimensionValue) {
    const parsedDimensionValue = dimensionValue.replace(/\s+/g, "");
    const url = `${CDN}/assets/img/retailers/${parsedDimensionValue}.png`;
    return {
      label: dimensionValue,
      overlayText: dimensionValue,
      value: dimensionValue,
      url,
    };
  } else if (
    dimensionTypeName === "Ad Name" &&
    dimensionValue &&
    adInfoMap &&
    adInfoMap[dimensionValue]
  ) {
    const adInfo = adInfoMap[dimensionValue];
    const url = `${CDN}/creativeAssets/metaThumbnails/${adInfo.company}/${adInfo.account_id}/${adInfo.ad_id}.jpeg`;
    return {
      label: dimensionValue,
      overlayText: dimensionValue,
      value: dimensionValue,
      url,
      adInfo,
    };
  } else if (dimensionTypeName === "Ad Name") {
    return {
      label: dimensionValue,
      overlayText: dimensionValue, // allows tooltip for longer ad names
      value: dimensionValue,
    };
  } else if (dimensionTypeName === "Date" || dimensionTypeName === "ComparisonDate") {
    if (!dimensionHeader.dateGrouping) {
      return defaultCell;
    }
    const capitalizedDateGrouping =
      dimensionHeader.dateGrouping.charAt(0).toUpperCase() + dimensionHeader.dateGrouping.slice(1);
    return {
      label: formatDateLabel(dimensionValue, fiscalCal, capitalizedDateGrouping),
      value: dimensionValue,
    };
  }
  return defaultCell;
};

export const getDataColMetaData = (
  channel: App2Channels,
  currencyCode: string
): ColumnMetaDataMap => {
  const items = getDataColForMetadata(channel);
  const overrideMap = CURRENCY_OVERRIDE_MAPS[currencyCode];

  const columnMetaDataMap = items.reduce((map, { name: metric }) => {
    if (sharedColumnMetaDataMap.hasOwnProperty(metric)) {
      if (overrideMap && overrideMap.hasOwnProperty(metric)) {
        map[metric] = overrideMap[metric];
      } else {
        map[metric] = sharedColumnMetaDataMap[metric];
      }
    }
    return map;
  }, {});
  return columnMetaDataMap;
};

export const getDimensionColMetaData = (channel: App2Channels): ColumnMetaDataMap => {
  return getDimensionColForMetadata(channel).reduce(
    (map, col) => {
      map[col.prettyName] = {
        contentType: "text",
        dimensionVarName: col.name,
        displayName: col.prettyName,
        iconStyle: getDimensionIconStyle(col.name),
      };
      return map;
    },
    { ...sharedDimensionColumnMetaDataMap }
  );
};
